// GPOS pair kerning (M14). opentype.js 1.3.4 reads GPOS kerning only from lookups of type 2; Montserrat,
// Poppins, Playfair Display, Lora and DM Serif Display wrap theirs in Extension lookups (type 9), so their
// kerning was silently lost (measured: "AV" 0 units). This reader takes the font file's raw GPOS table
// (WOFF tables are inflated with fflate), unwraps Extension lookups, and answers PairPos format 1 and 2
// x-advance adjustments for every lookup of the 'kern' feature. Fonts with no GPOS kern fall back to the
// old 'kern' table through opentype.js.
import { unzlibSync } from 'fflate';

export type KernFn = (left: number, right: number) => number;

/** Raw table bytes from a TTF/OTF or WOFF (1.0) file. */
export function tableBytes(file: Uint8Array, tag: string): DataView | null {
  const dv = new DataView(file.buffer, file.byteOffset, file.byteLength);
  const sig = String.fromCharCode(file[0]!, file[1]!, file[2]!, file[3]!);
  if (sig === 'wOFF') {
    const n = dv.getUint16(12);
    for (let i = 0; i < n; i++) {
      const o = 44 + i * 20;
      const t = String.fromCharCode(file[o]!, file[o + 1]!, file[o + 2]!, file[o + 3]!);
      if (t !== tag) continue;
      const off = dv.getUint32(o + 4),
        comp = dv.getUint32(o + 8),
        orig = dv.getUint32(o + 12);
      const raw = file.subarray(off, off + comp);
      const data = comp < orig ? unzlibSync(raw) : raw;
      return new DataView(data.buffer, data.byteOffset, data.byteLength);
    }
    return null;
  }
  const n = dv.getUint16(4);
  for (let i = 0; i < n; i++) {
    const o = 12 + i * 16;
    const t = String.fromCharCode(file[o]!, file[o + 1]!, file[o + 2]!, file[o + 3]!);
    if (t === tag)
      return new DataView(file.buffer, file.byteOffset + dv.getUint32(o + 8), dv.getUint32(o + 12));
  }
  return null;
}

const VALUE_BITS = (f: number) => {
  let n = 0;
  for (let b = 0; b < 8; b++) if (f & (1 << b)) n++;
  return n * 2;
};
/** x-advance inside a ValueRecord at `o` (0 when the format has none). */
function xAdvance(dv: DataView, o: number, format: number): number {
  if (!(format & 0x4)) return 0;
  let skip = 0;
  if (format & 0x1) skip += 2;
  if (format & 0x2) skip += 2;
  return dv.getInt16(o + skip);
}

function coverage(dv: DataView, o: number): Map<number, number> {
  const m = new Map<number, number>();
  const fmt = dv.getUint16(o);
  if (fmt === 1) {
    const n = dv.getUint16(o + 2);
    for (let i = 0; i < n; i++) m.set(dv.getUint16(o + 4 + i * 2), i);
  } else if (fmt === 2) {
    const n = dv.getUint16(o + 2);
    for (let i = 0; i < n; i++) {
      const r = o + 4 + i * 6;
      const start = dv.getUint16(r),
        end = dv.getUint16(r + 2),
        idx = dv.getUint16(r + 4);
      for (let g = start; g <= end; g++) m.set(g, idx + g - start);
    }
  }
  return m;
}

function classDef(dv: DataView, o: number): (g: number) => number {
  const fmt = dv.getUint16(o);
  if (fmt === 1) {
    const start = dv.getUint16(o + 2),
      n = dv.getUint16(o + 4);
    return (g) => (g >= start && g < start + n ? dv.getUint16(o + 6 + (g - start) * 2) : 0);
  }
  const n = dv.getUint16(o + 2);
  const ranges: [number, number, number][] = [];
  for (let i = 0; i < n; i++) {
    const r = o + 4 + i * 6;
    ranges.push([dv.getUint16(r), dv.getUint16(r + 2), dv.getUint16(r + 4)]);
  }
  return (g) => {
    let lo = 0,
      hi = ranges.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const [s, e, c] = ranges[mid]!;
      if (g < s) hi = mid - 1;
      else if (g > e) lo = mid + 1;
      else return c;
    }
    return 0;
  };
}

type Sub = (l: number, r: number) => number | undefined;

function pairPos(dv: DataView, o: number): Sub | null {
  const fmt = dv.getUint16(o);
  const cov = coverage(dv, o + dv.getUint16(o + 2));
  const vf1 = dv.getUint16(o + 4),
    vf2 = dv.getUint16(o + 6);
  const s1 = VALUE_BITS(vf1),
    s2 = VALUE_BITS(vf2);
  if (fmt === 1) {
    const sets = new Map<number, Map<number, number>>();
    for (const [g, idx] of cov) {
      const ps = o + dv.getUint16(o + 10 + idx * 2);
      const n = dv.getUint16(ps);
      const m = new Map<number, number>();
      for (let i = 0; i < n; i++) {
        const r = ps + 2 + i * (2 + s1 + s2);
        m.set(dv.getUint16(r), xAdvance(dv, r + 2, vf1));
      }
      sets.set(g, m);
    }
    // Left glyph covered but pair missing: the next subtable is tried (undefined).
    return (l, r) => sets.get(l)?.get(r);
  }
  if (fmt === 2) {
    const c1 = classDef(dv, o + dv.getUint16(o + 8)),
      c2 = classDef(dv, o + dv.getUint16(o + 10));
    const n1 = dv.getUint16(o + 12),
      n2 = dv.getUint16(o + 14);
    const rec = s1 + s2;
    return (l, r) => {
      if (!cov.has(l)) return undefined;
      const a = c1(l),
        b = c2(r);
      if (a >= n1 || b >= n2) return 0;
      return xAdvance(dv, o + 16 + (a * n2 + b) * rec, vf1);
    };
  }
  return null;
}

/** A pair-kerning function from a font file's GPOS 'kern' feature, or null when there is none. */
export function gposKerning(file: Uint8Array): KernFn | null {
  const dv = tableBytes(file, 'GPOS');
  if (!dv || dv.byteLength < 10) return null;
  const featureList = dv.getUint16(6),
    lookupList = dv.getUint16(8);
  const lookupIdx = new Set<number>();
  const nf = dv.getUint16(featureList);
  for (let i = 0; i < nf; i++) {
    const r = featureList + 2 + i * 6;
    const tag = String.fromCharCode(
      dv.getUint8(r),
      dv.getUint8(r + 1),
      dv.getUint8(r + 2),
      dv.getUint8(r + 3),
    );
    if (tag !== 'kern') continue;
    const f = featureList + dv.getUint16(r + 4);
    const n = dv.getUint16(f + 2);
    for (let k = 0; k < n; k++) lookupIdx.add(dv.getUint16(f + 4 + k * 2));
  }
  const subs: Sub[] = [];
  for (const li of [...lookupIdx].sort((a, b) => a - b)) {
    const lo = lookupList + dv.getUint16(lookupList + 2 + li * 2);
    const type = dv.getUint16(lo);
    const n = dv.getUint16(lo + 4);
    for (let s = 0; s < n; s++) {
      let so = lo + dv.getUint16(lo + 6 + s * 2);
      let t = type;
      if (t === 9) {
        t = dv.getUint16(so + 2);
        so += dv.getUint32(so + 4);
      }
      if (t !== 2) continue;
      const p = pairPos(dv, so);
      if (p) subs.push(p);
    }
  }
  if (!subs.length) return null;
  return (l, r) => {
    for (const s of subs) {
      const v = s(l, r);
      if (v !== undefined) return v;
    }
    return 0;
  };
}
